import { useCallback, useRef, useState } from 'react';

interface SpeechInputState {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
}

interface UseSpeechInputOptions {
  onFinalTranscript?: (text: string) => void;
  onInterimTranscript?: (text: string) => void;
  continuous?: boolean;
  lang?: string;
}

export function useSpeechInput(options: UseSpeechInputOptions = {}) {
  const {
    onFinalTranscript,
    onInterimTranscript,
    continuous = true,
    lang = 'en-US'
  } = options;

  const [state, setState] = useState<SpeechInputState>({
    isListening: false,
    transcript: '',
    interimTranscript: '',
    error: null
  });

  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef<string>('');
  const processedIndicesRef = useRef<Set<number>>(new Set());
  const startingRef = useRef<boolean>(false);

  const isSupported = typeof window !== 'undefined' &&
    ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  const start = useCallback(() => {
    if (!isSupported) {
      setState(prev => ({ ...prev, error: 'Speech recognition not supported' }));
      return;
    }

    // Prevent double-start race condition
    if (startingRef.current) {
      console.log('[useSpeechInput] Already starting, ignoring');
      return;
    }
    startingRef.current = true;

    // Cleanup any existing instance
    if (recognitionRef.current) {
      console.log('[useSpeechInput] Cleaning up existing instance');
      try { recognitionRef.current.abort(); } catch {}
      recognitionRef.current = null;
    }

    // Reset tracking state
    finalTranscriptRef.current = '';
    processedIndicesRef.current = new Set();

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    const recognition = new SpeechRecognition();
    console.log('[useSpeechInput] Created new recognition instance');

    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('[useSpeechInput] onstart fired');
      startingRef.current = false;
      setState(prev => ({
        ...prev,
        isListening: true,
        transcript: '',
        interimTranscript: '',
        error: null
      }));
    };

    recognition.onresult = (event: any) => {
      let interim = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];

        if (result.isFinal) {
          // Deduplicate - Chrome fires duplicate final events
          if (!processedIndicesRef.current.has(i)) {
            processedIndicesRef.current.add(i);
            const text = result[0].transcript;
            finalTranscriptRef.current += text;
            onFinalTranscript?.(text);
          }
        } else {
          // Latest interim only (replaces previous interim)
          interim = result[0].transcript;
        }
      }

      const fullTranscript = finalTranscriptRef.current + interim;

      setState(prev => ({
        ...prev,
        transcript: finalTranscriptRef.current,
        interimTranscript: interim
      }));

      onInterimTranscript?.(fullTranscript);
    };

    recognition.onerror = (event: any) => {
      console.log('[useSpeechInput] onerror:', event.error);
      startingRef.current = false;
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setState(prev => ({
          ...prev,
          error: `Speech error: ${event.error}`,
          isListening: false
        }));
      }
    };

    recognition.onend = () => {
      console.log('[useSpeechInput] onend fired');
      startingRef.current = false;
      setState(prev => ({ ...prev, isListening: false }));
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
      console.log('[useSpeechInput] start() called successfully');
    } catch (e) {
      console.error('[useSpeechInput] Failed to start:', e);
      startingRef.current = false;
      setState(prev => ({ ...prev, error: `Failed to start: ${e}` }));
    }
  }, [isSupported, continuous, lang, onFinalTranscript, onInterimTranscript]);

  const stop = useCallback(() => {
    console.log('[useSpeechInput] stop() called');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }, []);

  const abort = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setState(prev => ({ ...prev, isListening: false }));
  }, []);

  const getFullTranscript = useCallback(() => {
    return finalTranscriptRef.current;
  }, []);

  return {
    ...state,
    isSupported,
    start,
    stop,
    abort,
    getFullTranscript
  };
}

export default useSpeechInput;
